'use client';

import { invariant } from '@ifixit/helpers';
import { useBodyScrollLock } from '@ifixit/ui/hooks/use-body-scroll-lock';
import { useControllableState } from '@ifixit/ui/hooks/use-controllable-state';
import { useFocusTrap } from '@ifixit/ui/hooks/use-focus-trap';
import { useKeyDown } from '@ifixit/ui/hooks/use-key-down';
import { useMounted } from '@ifixit/ui/hooks/use-mounted';
import { cn } from '@ifixit/ui/lib/utils';
import { Slot } from '@radix-ui/react-slot';
import {
   createContext,
   Fragment,
   useCallback,
   useContext,
   useEffect,
   useId,
   useRef,
   useState,
   type ComponentProps,
   type ReactNode,
   type RefObject,
} from 'react';
import { createPortal } from 'react-dom';

const SHEET_EXIT_FALLBACK_SLACK_MS = 50;

interface SheetContextValue {
   open: boolean;
   setOpen: (open: boolean) => void;
   isPresent: boolean;
   openCount: number;
   completeExit: () => void;
   contentId: string;
   titleId: string;
   descriptionId: string;
   closeButtonRef: RefObject<HTMLButtonElement | null>;
   claimInitialFocus: () => boolean;
}

const SheetContext = createContext<SheetContextValue | null>(null);

function useSheetContext() {
   const context = useContext(SheetContext);
   invariant(context, 'Sheet components must be used within a Sheet');
   return context;
}

export function useSheet() {
   const context = useSheetContext();
   return {
      isOpen: context.open,
      open: () => context.setOpen(true),
      close: () => context.setOpen(false),
      toggle: () => context.setOpen(!context.open),
   };
}

interface SheetProps {
   children: ReactNode;
   open?: boolean;
   onOpenChange?: (open: boolean) => void;
   defaultOpen?: boolean;
}

function Sheet({ children, open, onOpenChange, defaultOpen = false }: SheetProps) {
   const [openState, setOpen] = useControllableState({
      prop: open,
      defaultProp: defaultOpen,
      onChange: onOpenChange,
   });

   const { isPresent, openCount, completeExit } = useSheetPresence(openState);

   const id = useId();
   const contentId = `${id}-content`;
   const titleId = `${id}-title`;
   const descriptionId = `${id}-description`;
   const closeButtonRef = useRef<HTMLButtonElement>(null);

   const initialFocusClaimRef = useRef(-1);
   const claimInitialFocus = useCallback(() => {
      if (initialFocusClaimRef.current === openCount) return false;
      initialFocusClaimRef.current = openCount;
      return true;
   }, [openCount]);

   return (
      <SheetContext.Provider
         value={{
            open: openState,
            setOpen,
            isPresent,
            openCount,
            completeExit,
            contentId,
            titleId,
            descriptionId,
            closeButtonRef,
            claimInitialFocus,
         }}
      >
         {children}
      </SheetContext.Provider>
   );
}

interface SheetTriggerProps extends ComponentProps<'button'> {
   asChild?: boolean;
}

function SheetTrigger({ onClick, asChild = false, ...props }: SheetTriggerProps) {
   const context = useSheetContext();
   const sheet = useSheet();
   const Comp = asChild ? Slot : 'button';

   return (
      <Comp
         data-slot="sheet-trigger"
         aria-haspopup="dialog"
         aria-expanded={sheet.isOpen}
         aria-controls={context.contentId}
         onClick={event => {
            onClick?.(event);
            sheet.open();
         }}
         {...props}
      />
   );
}

interface SheetCloseProps extends ComponentProps<'button'> {
   asChild?: boolean;
}

function SheetClose({ onClick, asChild = false, ...props }: SheetCloseProps) {
   const context = useSheetContext();
   const sheet = useSheet();
   const Comp = asChild ? Slot : 'button';

   useClaimInitialFocus();

   return (
      <Comp
         // eslint-disable-next-line react-hooks/refs
         ref={context.closeButtonRef}
         data-slot="sheet-close"
         onClick={e => {
            onClick?.(e);
            sheet.close();
         }}
         {...props}
      />
   );
}

interface SheetPortalProps {
   children: ReactNode;
   container?: HTMLElement | null;
}

function SheetPortal({ children, container }: SheetPortalProps) {
   const mounted = useMounted();

   if (!mounted) return null;

   return createPortal(children, container || document.body);
}

function SheetOverlay({ className, onClick, ...props }: ComponentProps<'div'>) {
   const { isOpen, close } = useSheet();

   return (
      <div
         data-slot="sheet-overlay"
         data-state={isOpen ? 'open' : 'closed'}
         className={cn(
            // z-sheet-overlay sits one tier below z-sheet so content-above-overlay
            // is encoded in the value, not sibling render order.
            'fixed inset-0 z-sheet-overlay bg-black/50 transition-[opacity,visibility] duration-300',
            'data-[state=closed]:opacity-0 data-[state=closed]:invisible data-[state=closed]:pointer-events-none data-[state=open]:opacity-100',
            className
         )}
         onClick={e => {
            onClick?.(e);
            close();
         }}
         {...props}
      />
   );
}

interface SheetContentProps extends ComponentProps<'div'> {
   side?: 'top' | 'right' | 'bottom' | 'left';
}

function SheetContent({
   className,
   children,
   side = 'right',
   onTransitionEnd,
   ref,
   ...props
}: SheetContentProps) {
   const context = useSheetContext();
   const sheet = useSheet();
   const contentRef = useFocusTrap<HTMLDivElement>({
      enabled: sheet.isOpen,
      initialFocusRef: context.closeButtonRef,
   });
   const setRefs = useCallback(
      (node: HTMLDivElement | null) => {
         contentRef.current = node;
         if (typeof ref === 'function') ref(node);
         else if (ref != null) ref.current = node;
      },
      [contentRef, ref]
   );

   useKeyDown(
      event => {
         // A Radix layer open inside the sheet (DropdownMenu) handles
         // Escape first, in the capture phase, and marks it
         // defaultPrevented. That press closes the popup only; the next one
         // closes the sheet. An open Select or Popover marks it earlier
         // still, on the window.
         if (event.key === 'Escape' && !event.defaultPrevented) sheet.close();
      },
      { enabled: sheet.isOpen }
   );

   useBodyScrollLock(sheet.isOpen);

   useExitFallback();

   return (
      <SheetPortal>
         <SheetOverlay />
         <div
            ref={setRefs}
            id={context.contentId}
            role="dialog"
            aria-modal="true"
            aria-labelledby={context.titleId}
            aria-describedby={context.descriptionId}
            tabIndex={-1}
            data-slot="sheet-content"
            data-state={sheet.isOpen ? 'open' : 'closed'}
            onTransitionEnd={event => {
               onTransitionEnd?.(event);
               if (event.target === event.currentTarget && !sheet.isOpen) {
                  context.completeExit();
               }
            }}
            className={cn(
               // Hide with `visibility` in addition to `translate`: browsers
               // without `translate` support would otherwise show the closed
               // sheet, and off-screen content stays in the tab order.
               // Transitioning visibility delays the flip until the slide ends.
               'bg-background fixed z-sheet flex flex-col gap-4 shadow-lg transition-[translate,visibility] duration-300 ease-in-out data-[state=closed]:invisible',
               side === 'right' &&
                  'inset-y-0 right-0 h-full w-3/4 border-l pr-[env(safe-area-inset-right)] sm:max-w-sm data-[state=closed]:translate-x-full data-[state=open]:translate-x-0',
               side === 'left' &&
                  'inset-y-0 left-0 h-full w-3/4 border-r pl-[env(safe-area-inset-left)] sm:max-w-sm data-[state=closed]:-translate-x-full data-[state=open]:translate-x-0',
               side === 'top' &&
                  'inset-x-0 top-0 h-auto border-b data-[state=closed]:-translate-y-full data-[state=open]:translate-y-0',
               side === 'bottom' &&
                  'inset-x-0 bottom-0 h-auto border-t pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)] data-[state=closed]:translate-y-full data-[state=open]:translate-y-0',
               className
            )}
            {...props}
         >
            {children}
         </div>
      </SheetPortal>
   );
}

function SheetPresence({ children }: { children: ReactNode }) {
   const context = useSheetContext();
   if (!context.isPresent) return null;
   return <Fragment key={context.openCount}>{children}</Fragment>;
}

function SheetHeader({ className, ...props }: ComponentProps<'div'>) {
   return (
      <div
         data-slot="sheet-header"
         className={cn('flex flex-col gap-1.5 p-4', className)}
         {...props}
      />
   );
}

function SheetFooter({ className, ...props }: ComponentProps<'div'>) {
   return (
      <div
         data-slot="sheet-footer"
         className={cn(
            'mt-auto flex flex-col gap-2 p-4 pb-[max(1rem,env(safe-area-inset-bottom))]',
            className
         )}
         {...props}
      />
   );
}

interface SheetTitleProps extends ComponentProps<'h2'> {
   asChild?: boolean;
}

function SheetTitle({ className, asChild = false, ...props }: SheetTitleProps) {
   const context = useSheetContext();
   const Comp = asChild ? Slot : 'h2';
   return (
      <Comp
         id={context.titleId}
         data-slot="sheet-title"
         className={cn('text-foreground font-semibold', className)}
         {...props}
      />
   );
}

interface SheetDescriptionProps extends ComponentProps<'p'> {
   asChild?: boolean;
}

function SheetDescription({ className, asChild = false, ...props }: SheetDescriptionProps) {
   const context = useSheetContext();
   const Comp = asChild ? Slot : 'p';
   return (
      <Comp
         id={context.descriptionId}
         data-slot="sheet-description"
         className={cn('text-muted-foreground text-sm', className)}
         {...props}
      />
   );
}

function useSheetPresence(open: boolean) {
   const [prevOpen, setPrevOpen] = useState(open);
   const [isExiting, setIsExiting] = useState(false);
   const [openCount, setOpenCount] = useState(0);
   if (open !== prevOpen) {
      setPrevOpen(open);
      setIsExiting(!open);
      if (open) setOpenCount(openCount + 1);
   }

   const completeExit = useCallback(() => setIsExiting(false), []);

   return { isPresent: open || isExiting, openCount, completeExit };
}

function useClaimInitialFocus() {
   const { open, closeButtonRef, claimInitialFocus } = useSheetContext();
   useEffect(() => {
      if (!open) return;
      const button = closeButtonRef.current;
      if (!button) return;
      if (!claimInitialFocus()) return;
      const container = button.closest('[data-slot="sheet-content"]');
      const activeElement = document.activeElement;
      if (activeElement === document.body || activeElement === container) {
         button.focus();
      }
   }, [open, closeButtonRef, claimInitialFocus]);
}

function useExitFallback() {
   const { open, isPresent, completeExit, contentId } = useSheetContext();
   useEffect(() => {
      if (open || !isPresent) return;
      const panel = document.querySelector<HTMLElement>(`[id="${contentId}"]`);
      if (!panel) return;
      const fallback = setTimeout(
         completeExit,
         maxTransitionMs(panel) + SHEET_EXIT_FALLBACK_SLACK_MS
      );
      return () => clearTimeout(fallback);
   }, [open, isPresent, completeExit, contentId]);
}

function maxTransitionMs(element: HTMLElement) {
   const style = getComputedStyle(element);
   const durations = style.transitionDuration.split(',').map(parseCssTimeMs);
   const delays = style.transitionDelay.split(',').map(parseCssTimeMs);
   return Math.max(0, ...durations) + Math.max(0, ...delays);
}

function parseCssTimeMs(value: string) {
   const trimmed = value.trim();
   if (!trimmed) return 0;
   const number = parseFloat(trimmed);
   if (!Number.isFinite(number)) return 0;
   return trimmed.endsWith('ms') ? number : number * 1000;
}

export {
   Sheet,
   SheetClose,
   SheetContent,
   SheetDescription,
   SheetFooter,
   SheetHeader,
   SheetPresence,
   SheetTitle,
   SheetTrigger,
};
